import { useEffect, useState } from "react";
import { FiArrowUpRight, FiChevronLeft, FiChevronRight, FiRefreshCw, FiSearch } from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import { getMarketingBroadcastHistory } from "../../../../../api/adminMarketingBroadcast";
import { ComponentLoader } from "../../../../../components/loaders";
import { readAdminPageState, saveAdminPageState } from "../../../../../utils/adminPageState";

const formatLabel = (value) => String(value || "Not specified").replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const formatDate = (value) => value ? new Date(value).toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" }) : "Not available";
const plainText = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
const dateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const historyPageDefaults = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 7);
  return { channel: "all", audience: "", segment: "all", dispatchedBy: "", startDate: dateInputValue(startDate), endDate: dateInputValue(endDate) };
};

const HistoryTable = ({ history }) => <div className="history-table-wrap"><table className="history-table"><thead><tr><th>Broadcast</th><th>Audience</th><th>Delivery</th><th>Dispatched by</th><th>Date</th></tr></thead><tbody>{history.map((item) => {
  const summary = item.summary || {};
  return <tr key={item.broadcast_id}>
    <td data-label="Broadcast"><div className="history-message"><span className={`channel-badge ${item.channel}`}>{formatLabel(item.channel)}</span><strong>{item.title || "Untitled broadcast"}</strong><span>{plainText(item.message) || "No message content"}</span></div></td>
    <td data-label="Audience"><strong>{formatLabel(item.audience)}</strong><span>{item.target === "segment" ? formatLabel(item.segment) : "Custom selection"}</span></td>
    <td data-label="Delivery"><strong>{Number(summary.sent_count || 0).toLocaleString()} sent</strong><span>{Number(summary.total_targeted || 0).toLocaleString()} targeted · {Number(summary.failed_count || 0).toLocaleString()} failed · {Number(summary.skipped_count || 0).toLocaleString()} skipped</span></td>
    <td data-label="Dispatched by"><strong>{item.dispatched_by?.name || "Admin"}</strong><span>{item.dispatched_by?.email || "Email unavailable"}</span></td>
    <td data-label="Date"><strong>{formatDate(item.dispatched_at)}</strong></td>
  </tr>;
})}</tbody></table></div>;

export const BroadcastHistoryPreview = ({ history = [], totalItems = 0, loading = false, error = "", onRetry}) => <section className="content-card broadcast-history history-preview">
  <div className="card-heading history-heading">
    <div><h2>Recent broadcasts</h2><p>The first page of your latest broadcast activity.</p></div>
    <Link className="history-view-more" to="/admin/internal/marketing/broadcast/history">View all history <FiArrowUpRight aria-hidden="true" /></Link>
  </div>
  {loading ? <div className="history-loader"><ComponentLoader /></div> : error ? <div className="history-error" role="alert"><span>{error}</span><button type="button" onClick={onRetry}>Try again</button></div> : history.length ? <>
    <HistoryTable history={history} />
    <footer className="history-preview-footer"><span>Showing {history.length.toLocaleString()} of {totalItems.toLocaleString()} broadcasts</span>
    </footer>
  </> : <div className="history-empty"><strong>No broadcast history</strong><span>No broadcasts have been dispatched yet.</span></div>}
</section>;

export const BroadcastHistorySection = () => {
  const location = useLocation();
  const [initialPageState] = useState(() => readAdminPageState(location.pathname, historyPageDefaults()));
  const [channel, setChannel] = useState(initialPageState.channel);
  const [audience, setAudience] = useState(initialPageState.audience);
  const [segment, setSegment] = useState(initialPageState.segment);
  const [dispatchedBy, setDispatchedBy] = useState(initialPageState.dispatchedBy);
  const [debouncedDispatchedBy, setDebouncedDispatchedBy] = useState(initialPageState.dispatchedBy);
  const [startDate, setStartDate] = useState(initialPageState.startDate);
  const [endDate, setEndDate] = useState(initialPageState.endDate);
  const [page, setPage] = useState(1);
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState({ total_items: 0, current_page: 1, total_pages: 1, pageSize: 10 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedDispatchedBy(dispatchedBy);
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [dispatchedBy]);

  useEffect(() => {
    saveAdminPageState(location.pathname, { channel, audience, segment, dispatchedBy, startDate, endDate });
  }, [audience, channel, dispatchedBy, endDate, location.pathname, segment, startDate]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getMarketingBroadcastHistory({ channel, audience, segment, dispatchedBy: debouncedDispatchedBy, startDate, endDate, page, pageSize: 10 })
      .then((response) => {
        if (!active) return;
        setHistory(response.data);
        setPagination(response.pagination);
      })
      .catch((requestError) => {
        if (!active) return;
        setHistory([]);
        setError(requestError?.response?.data?.message || requestError?.message || "Broadcast history could not be loaded.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [audience, channel, debouncedDispatchedBy, endDate, page, refreshKey, segment, startDate]);

  const updateFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  return <section className="content-card broadcast-history">
    <div className="card-heading history-heading"><div><h2>Broadcast history</h2><p>Previously dispatched push notifications, SMS messages, and emails.</p></div></div>
    <div className="history-filter-bar">
      <label><span>Channel</span><select value={channel} onChange={updateFilter(setChannel)}><option value="all">All channels</option><option value="push">Push notification</option><option value="sms">SMS</option><option value="email">Email</option></select></label>
      <label><span>Audience</span><select value={audience} onChange={updateFilter(setAudience)}><option value="">All audiences</option><option value="registered_users">Registered users</option><option value="guest_users">Guest users</option><option value="farms">Farms</option></select></label>
      <label><span>Segment</span><select value={segment} onChange={updateFilter(setSegment)}><option value="all">All segments</option><option value="frequent_buyers">Frequent buyers</option><option value="inactive_buyers">Inactive buyers</option><option value="new_users">New users</option><option value="new_farms">New farms</option><option value="verified_farms">Verified farms</option><option value="custom_selection">Custom selection</option></select></label>
      <div className="history-date-range">
        <label><span>Start date</span><input type="date" value={startDate} max={endDate} onChange={(event) => { const value = event.target.value; setStartDate(value); if (value > endDate) setEndDate(value); setPage(1); }} /></label>
        <label><span>End date</span><input type="date" value={endDate} min={startDate} onChange={(event) => { const value = event.target.value; setEndDate(value); if (value < startDate) setStartDate(value); setPage(1); }} /></label>
      </div>
      <label className="history-admin-search"><span>Dispatched by</span><div><FiSearch aria-hidden="true" /><input value={dispatchedBy} onChange={(event) => setDispatchedBy(event.target.value)} placeholder="Admin name, email, or ID" /></div></label>
      <button className="history-refresh" type="button" onClick={() => setRefreshKey((current) => current + 1)} disabled={loading} aria-label="Refresh broadcast history"><FiRefreshCw aria-hidden="true" /></button>
    </div>
    {loading ? <div className="history-loader"><ComponentLoader /></div> : error ? <div className="history-error" role="alert"><span>{error}</span><button type="button" onClick={() => setRefreshKey((current) => current + 1)}>Try again</button></div> : history.length ? <>
      <HistoryTable history={history} />
      <footer className="history-pagination"><span>{pagination.total_items.toLocaleString()} broadcast{pagination.total_items === 1 ? "" : "s"}</span><div><button type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)} aria-label="Previous history page"><FiChevronLeft /></button><span>Page {pagination.current_page} of {pagination.total_pages}</span><button type="button" disabled={page >= pagination.total_pages} onClick={() => setPage((current) => current + 1)} aria-label="Next history page"><FiChevronRight /></button></div></footer>
    </> : <div className="history-empty"><strong>No broadcast history</strong><span>No broadcasts match the selected filters.</span></div>}
  </section>;
};

